/** @jsxImportSource @emotion/react */
import type React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import { FloatingInput } from '../../../components/floating-input/floating-input';
import {
  // Card,
  ContinueButton,
  EmailStrong,
  ErrorText,
  ForgotLink,
  FullWidth,
  Headline,
  PromptText,
  Screen,
  SpinnerWrap,
  Subhead,
} from './password-login.styled';
import { usePasswordLogin } from './password-login.hooks';
import { VSpace } from '../../chat/settings/account/account-settings';
import type { LoginStepProps } from '../login-shell.types';

export const PasswordLogin: React.FC<LoginStepProps> = ({ go, data, setData }) => {
  const navigate = useNavigate();

  const { email, password, setPassword, loading, error, submit } = usePasswordLogin({
    onSuccess: () => {
      // navigate to /chat after successful auth
      navigate('/chat', { replace: true });
    },
  });

  useEffect(() => {
    if (email && !data.email) {
      setData({ email });
    }
  }, [email, data.email, setData]);

  const emailForCopy = email?.trim() || 'email@emailplaceholder.com';

  const handleForgot = () => {
    if (loading) return;
    go('forgot-password');
  };

  const handleSubmit = () => {
    if (loading) return;
    void submit();
  };

  const handleEditEmail = () => {
    if (loading) return;
    go('email-login');
  };

  return (
    <Screen data-testid="password-login-screen">
      <div>
        <VSpace $h={32} />
        <Headline>
          Judgment. <br />
          Refined.
        </Headline>
        <VSpace $h={16} />
        <Subhead>The long conversations that create enterprise value.</Subhead>
      </div>
      <div>
        <PromptText>
          {/* add edit email icon: edit-email-icon */}
          Enter the password associated with your account <EmailStrong>
            {emailForCopy}
          </EmailStrong>{' '}
          <img
            style={{ width: '16px', verticalAlign: 'middle', marginLeft: '6px', cursor: 'pointer' }}
            src="/edit-email-icon.svg"
            alt="Edit email"
            onClick={handleEditEmail}
          />
        </PromptText>

        <VSpace $h={22} />

        <FullWidth>
          {/* <Card> */}
          <FloatingInput
            id="password"
            label="Enter your password"
            placeholder=""
            type="password"
            value={password}
            onChange={(e: any) => setPassword(e?.target?.value ?? '')}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' && !loading) {
                e.preventDefault?.();
                void submit();
              }
            }}
            disabled={loading}
          />

          <VSpace $h={16} />

          <ContinueButton
            onClick={handleSubmit}
            disabled={loading}
            data-testid="password-login-submit"
          >
            Continue
          </ContinueButton>

          {loading && (
            <SpinnerWrap data-testid="password-login-loading">
              <UniversalSpinner />
            </SpinnerWrap>
          )}

          {!loading && error && (
            <>
              <VSpace $h={12} />
              <ErrorText data-testid="password-login-error">{error}</ErrorText>
            </>
          )}

          <VSpace $h={35} />

          <ForgotLink onClick={handleForgot} disabled={loading} data-testid="password-login-forgot">
            Forgot your password?
          </ForgotLink>

          <VSpace $h={25} />
          {/* </Card> */}
        </FullWidth>
      </div>
    </Screen>
  );
};
